import moment from 'moment'
import { useEffect, useState } from 'react'
import { NEW_PATIENT_VALUE_QUERY, PRODUCTION_VALUE_QUERY } from './graphql'
import { QueueValueStats } from './queueRegistry'
import { gqlQuery } from 'app/lib/graphqlClient'
import type { ActionCenterNewPatientValueQuery, ActionCenterProductionValueQuery } from 'app/generated/gql/graphql'
import { debug } from 'lib/logging'

/**
 * Fetches the workspace's real production averages (trailing 12 months) used to
 * price Action Center queues. Cached per workspace for the session; a resolver
 * failure (no PMS data, no permission) just leaves that figure undefined and
 * the UI degrades to counts-only framing — never a fabricated number.
 */

const cache = new Map<string, Promise<QueueValueStats>>()

async function fetchValueStats(locationIds?: string[]): Promise<QueueValueStats> {
  const dateRange = {
    fromDate: moment().subtract(12, `months`).format(`YYYY-MM-DD`),
    toDate: moment().format(`YYYY-MM-DD`),
  }
  const locations = locationIds?.length ? locationIds : undefined
  const [newPatients, production] = await Promise.all([
    gqlQuery<ActionCenterNewPatientValueQuery>(NEW_PATIENT_VALUE_QUERY, { dateRange, locationIds: locations }),
    gqlQuery<ActionCenterProductionValueQuery>(PRODUCTION_VALUE_QUERY, { dateRange, locationIds: locations }),
  ])
  const avgNewPatientVisitValue = newPatients.data?.newPatientsMetrics?.summary?.averageProductionPerNewPatientVisit
  const stats = production.data?.revenueMetrics?.stats
  const avgProductionPerVisit = stats?.avgNetProductionPerVisit || stats?.avgGrossProductionPerVisit
  debug(`action-center`, `Value stats`, { avgNewPatientVisitValue, avgProductionPerVisit })
  return {
    avgNewPatientVisitValue:
      avgNewPatientVisitValue && avgNewPatientVisitValue > 0 ? avgNewPatientVisitValue : undefined,
    avgProductionPerVisit: avgProductionPerVisit && avgProductionPerVisit > 0 ? avgProductionPerVisit : undefined,
  }
}

export function useQueueValueStats(
  workspaceId: string | undefined,
  locationIds?: string[],
): { stats: QueueValueStats; loading: boolean } {
  const [stats, setStats] = useState<QueueValueStats>({})
  const [loading, setLoading] = useState(true)
  const locationKey = locationIds?.join(`,`) ?? ``

  useEffect(() => {
    let cancelled = false
    const key = `${workspaceId ?? `org`}|${locationKey}`
    if (!cache.has(key)) cache.set(key, fetchValueStats(locationKey ? locationKey.split(`,`) : undefined))
    cache
      .get(key)!
      .then((result) => {
        if (cancelled) return
        setStats(result)
        setLoading(false)
      })
      .catch(() => {
        if (cancelled) return
        setStats({})
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [workspaceId, locationKey])

  return { stats, loading }
}
